import { Alert, Paper, Skeleton, Stack } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { AnalyticsDatetime } from "../utils/analytics-datetime";
import { useFloatingOutboxRecipients } from "./hooks/use-floating-outbox-recipients";

import type { FloatingOutboxRecipient } from "../../../modules/stats/server/analytics-service";
import type { ISO8601 } from "../../../utils/iso8601";
import type { AnalyticsMoment } from "./types";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import type { FC } from "react";

export const MomentOutboxTab: FC<{ moment: AnalyticsMoment }> = ({ moment }) => {
  const { data, loading, error } = useFloatingOutboxRecipients(moment);

  if (error) return <Alert severity="error">Unable to load outbox recipients</Alert>;

  return (
    <Paper elevation={6} sx={{ px: 3, py: 2 }}>
      {loading ? (
        <Skeleton
          animation="wave"
          variant="rounded"
          height={400}
          data-testid="outbox-tab-skeleton"
        />
      ) : (
        <OutboxDataGrid data={data} />
      )}
    </Paper>
  );
};

const OutboxDataGrid: FC<{ data: FloatingOutboxRecipient[] }> = ({ data }) => {
  const rows = useMemo(
    () =>
      data.map((item, id) => ({
        id,
        first_name: item.recipient_data?.first_name ?? "-",
        last_name: item.recipient_data?.last_name ?? "-",
        work_email: item.recipient_data?.email ?? "-",
        sent_to: item.recipient_data?.display ?? "-",
        created_at: item.created_at,
      })),
    [data],
  );

  const columns = useMemo(() => {
    const columnData: GridColDef<(typeof rows)[0]>[] = [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
      {
        field: "work_email",
        headerName: "Work Email",
        flex: 1,
      },
      {
        field: "sent_to",
        headerName: "Sent To",
        flex: 1,
      },
      {
        field: "created_at",
        headerName: "Moved to Outbox",
        flex: 1.5,
        renderCell: (params: GridRenderCellParams<(typeof rows)[0], ISO8601>) =>
          params.value ? (
            <Stack direction="row" spacing={1} alignItems="center" height="100%">
              <AnalyticsDatetime datetime={params.value} />
            </Stack>
          ) : null,
      },
    ];
    return columnData;
  }, []);

  return (
    <Stack spacing={1} direction="column">
      <Alert severity="info">
        These recipients are currently queued to send and should move to Delivered or Issues in the
        Analytics tab once they have been sent. This view is only available to ChangeEngine staff.
      </Alert>
      <DataGridPro
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
      />
    </Stack>
  );
};
